
import { useState } from "react";
import { Shell } from "@/components/layout/Shell";
import { useContractTypes } from "@/hooks/useContractTypes";
import { ContractTypeDeleteDialog } from "@/components/contract-types/ContractTypeDeleteDialog";
import { ContractType } from "@/lib/types";
import { DeleteAllConfirmationDialog } from "@/components/common/DeleteAllConfirmationDialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ContractTypesHeader } from "@/components/contract-types/ContractTypesHeader";
import { ContractTypesList } from "@/components/contract-types/ContractTypesList";

export default function ContractTypesPage() {
  const { useContractTypesQuery, useDeleteContractTypeMutation } = useContractTypes();
  const { data: contractTypes, isLoading, error } = useContractTypesQuery();
  const [contractTypeToDelete, setContractTypeToDelete] = useState<ContractType | null>(null);
  const deleteContractTypeMutation = useDeleteContractTypeMutation();
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const { t } = useTranslation();

  const handleDeleteClick = (type: ContractType) => {
    setContractTypeToDelete(type);
  };
  
  const handleDeleteAllContractTypes = async () => {
    if (!contractTypes || contractTypes.length === 0) return;
    
    try {
      setIsDeletingAll(true);
      
      // Delete contract types one by one
      for (const contractType of contractTypes) {
        await deleteContractTypeMutation.mutateAsync(contractType.id);
      }
      
      toast.success(t('contractTypes.allDeleted', 'All contract types have been deleted'));
    } catch (error) {
      console.error("Error deleting all contract types:", error);
      toast.error(t('contractTypes.deleteAllError', 'Failed to delete all contract types'));
    } finally {
      setIsDeletingAll(false);
    }
  };

  return (
    <Shell>
      <div className="space-y-6">
        <ContractTypesHeader 
          onDeleteAllClick={() => setDeleteAllDialogOpen(true)}
          hasContractTypes={!!(contractTypes && contractTypes.length > 0)}
        />

        <ContractTypesList
          contractTypes={contractTypes}
          isLoading={isLoading}
          error={error}
          onDeleteClick={handleDeleteClick}
        />
      </div>

      {contractTypeToDelete && (
        <ContractTypeDeleteDialog
          contractType={contractTypeToDelete}
          open={!!contractTypeToDelete}
          onOpenChange={(open) => {
            if (!open) setContractTypeToDelete(null);
          }}
        />
      )}

      {/* Delete All Confirmation Dialog */}
      <DeleteAllConfirmationDialog
        open={deleteAllDialogOpen}
        onOpenChange={setDeleteAllDialogOpen}
        onConfirm={handleDeleteAllContractTypes}
        title={t('contractTypes.deleteAllTitle', 'Delete all contract types?')}
        description={t('contractTypes.deleteAllDescription', 'This will permanently remove all contract types. This action cannot be undone.')}
        isDeleting={isDeletingAll}
      />
    </Shell>
  );
}
